import React, { useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { ForgotPasswordRequest } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { toast } from 'react-hot-toast';

// Styled components matching Regions design (reusing the same styling as LoginForm)
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const BRAND_COLOR = '#17A6DA';

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(2, 4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
  },
  paddingBottom: '100px',
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 60%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 200px',
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  //background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));


const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd', 
    },

    '&:hover fieldset': {
      borderColor: '#808080',
    },

    '&.Mui-focused fieldset': {
      borderColor:' #808080',
      borderWidth: '2px',
    },
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
  // [theme.breakpoints.down('sm')]: {
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  // },
}));



const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6 , 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

// Validation schema
const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email ID is required')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid email address'
    ),
});

// Initial form values
const initialValues: ForgotPasswordRequest = {
  email: '',
};

export const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword, loading } = useAuth();



  // Handle form submission
  const handleSubmit = async (values: ForgotPasswordRequest, { setFieldError }: { setFieldError: (field: string, message: string) => void }) => {
    
    const loadingToast = toast.loading(
      'Welcome back! Getting everything ready for you…',
      {
        position: 'top-center',
        icon: (
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Loading_2.gif?20170503175831"
            alt="loading"
            style={{
              width: '50px',   // change size here
              height: '50px',
              objectFit: 'contain',
              marginRight: '8px'
            }}
          />
        ),
        style: {
          marginTop: '40vh',
          transform: 'translateY(-50%)',

          background: '#ffffff',
          color: '#000',
          padding: '18px 24px',
          borderRadius: '12px',
          width: '620px',
          textAlign: 'center',

          borderTop: '4px solid #17A6DA',
          fontSize: '18px',
          fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }
    );

    try {
      await forgotPassword(values);
      toast.dismiss(loadingToast);
      //navigate('/auth/login', { replace: true });
      // We don't navigate after success as the user should see the success message
      // and follow instructions in the email
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Request failed';
      setFieldError('email', errorMessage);
    }
  };

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      {/* Left Panel - Forgot Password Form */}
      <LeftPanel>
        {/* Brand Header - Regions Style */}

        <FormSection>
          {/* Welcome Section - Regions Style */}
          <WelcomeSection>
            <Typography
              variant="h1"
              weight="bold"
              color="textPrimary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)' }}
            >
              Forgot Password
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
          </WelcomeSection>

          {/* Forgot Password Form */}
          <Box
            sx={{
              width: {
                xs: '320px', // Mobile devices
                sm: '420px', // Tablets
                md: '700px', // Desktops
                lg: '70%', // Large screens: full width
              },
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={forgotPasswordSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Email ID Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Email ID"
                          name="email"
                          type="text"
                          placeholder="Enter Email ID"
                          value={values.email}
                          onChange={e => setFieldValue('email', e.target.value)}
                          onBlur={() => setFieldTouched('email', true)}
                          onFocus={() => {
                            // Mark as touched when user focuses on the field
                            if (!touched.email) {
                              setFieldTouched('email', true);
                            }
                          }}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        fullWidth
                        loading={loading}
                        loadingText="Sending request..."
                        disabled={!values.email}
                        sx={{
                          backgroundColor: BRAND_COLOR,
                          color: '#fff',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          height: '50px',
                          '&:hover': {
                            backgroundColor: '#1392C1', // slightly darker
                          },
                          '&:disabled': {
                            backgroundColor: '#9bd3ea',
                            color: '#fff',
                          },
                        }}
                      >
                        Send Reset Link
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>

          {/* Footer Links */}
          <FooterLinks>
            <Link
              component={RouterLink}
              to="/auth/login"
              variant="body2"
              sx={{
                color: 'rgb(40, 42, 48)',
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Back to Login
            </Link>
          </FooterLinks>
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Forgot Password Banner" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default ForgotPasswordForm;
